import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header, Footer } from "@/components/Header";
import { api, mediaUrl } from "@/lib/api";
import { ArrowUpRight, Clock, ArrowLeft } from "lucide-react";

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function LookDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [activeImg, setActiveImg] = useState(null);

  useEffect(() => {
    api
      .get(`/looks/${id}`)
      .then((r) => {
        setData(r.data);
        setActiveImg(r.data.look.hero_thumbnail);
      })
      .catch(() => setErr(true));
  }, [id]);

  if (err)
    return (
      <div className="min-h-screen bg-[#0B0B0C]">
        <Header />
        <div className="pt-40 text-center">
          <p className="overline mb-4">Look unavailable</p>
          <Link to="/" className="text-[#D4AF37]">Return to Lookbook</Link>
        </div>
      </div>
    );
  if (!data) return <div className="min-h-screen bg-[#0B0B0C]" />;

  const { look, items } = data;
  const hasVideo = !!look.source_video;

  return (
    <div className="min-h-screen bg-[#0B0B0C] relative z-[2]">
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-28 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 overline mb-8 hover:text-[#D4AF37]" data-testid="back-to-lookbook">
          <ArrowLeft size={13} /> Lookbook
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Media */}
          <div className="lg:col-span-8">
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] lg:aspect-video overflow-hidden rounded-sm bg-[#141417] border border-white/10">
                {hasVideo ? (
                  <video
                    src={mediaUrl(look.source_video)}
                    controls
                    poster={mediaUrl(look.hero_thumbnail)}
                    className="w-full h-full object-cover"
                    data-testid="look-video-player"
                  />
                ) : (
                  <img src={mediaUrl(activeImg)} alt={look.title} className="w-full h-full object-cover" />
                )}
              </div>
              {!hasVideo && look.thumbnails?.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                  {look.thumbnails.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(t)}
                      data-testid={`look-thumb-${i}`}
                      className={`shrink-0 w-24 aspect-square overflow-hidden rounded-sm border transition-all ${
                        activeImg === t ? "border-[#D4AF37]" : "border-white/10 opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img src={mediaUrl(t)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tagged items */}
          <div className="lg:col-span-4">
            <p className="overline mb-3">{(look.category_summary || []).join(" · ")}</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light mb-6">{look.title}</h1>
            <div className="flex flex-wrap gap-2 mb-10">
              {(look.style_summary || []).map((s) => (
                <span key={s} className="text-xs px-3 py-1 rounded-full border border-white/15 text-[#8E8E9A]">
                  {s}
                </span>
              ))}
            </div>

            <p className="overline mb-5">Tagged Products — {items.length}</p>
            <div className="space-y-3" data-testid="look-tagged-items">
              {items.map(({ appearance, product }) => (
                <Link
                  key={appearance.id}
                  to={`/products/${product.id}`}
                  data-testid={`look-item-${product.id}`}
                  className="group flex gap-4 p-3 rounded-sm border border-white/5 bg-[#141417] hover:border-[#D4AF37]/40 transition-all duration-300"
                >
                  <div className="w-20 h-24 shrink-0 overflow-hidden rounded-sm">
                    <img src={mediaUrl(appearance.thumbnail || product.hero_thumbnail)} alt="" className="w-full h-full object-cover img-zoom" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="overline text-[10px]">{product.product_type}</p>
                    <h4 className="font-serif text-lg font-light leading-tight mt-1 group-hover:text-[#D4AF37] transition-colors">
                      {product.title}
                    </h4>
                    <div className="flex items-center gap-1 text-[#8E8E9A] text-xs mt-2 font-mono">
                      <Clock size={11} /> {fmtTime(appearance.timestamp_start)}
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="text-[#8E8E9A] group-hover:text-[#D4AF37] transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
